import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, Factory, Inbox, X } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { usePermissions } from '../contexts/permissions'
import { apiError, fmtDate, fmtDateTime } from '../lib/industria'
import {
  REQUEST_STATUS_BADGE, REQUEST_STATUS_LABEL,
  type RequestRow, type RequestStatus,
} from '../lib/producao'

const STATUSES: RequestStatus[] = ['requested', 'accepted', 'rejected', 'converted']

export function IndustriaSolicitacoesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('industria-solicitacoes', 'edit')

  const [status, setStatus] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [convert, setConvert] = useState<RequestRow | null>(null)
  const [form, setForm] = useState({ qtyPlanned: '', scheduledFor: '', expiryDate: '', notes: '' })

  const { data: requests = [], isLoading } = useQuery<RequestRow[]>({
    queryKey: ['producao', 'requests', status],
    queryFn: async () => (await api.get('/production-requests', {
      params: { status: status || undefined },
    })).data,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['producao'] })

  const decideMutation = useMutation({
    mutationFn: async (v: { id: string; status: 'accepted' | 'rejected'; decisionNotes?: string }) =>
      (await api.patch(`/production-requests/${v.id}`, {
        status: v.status,
        decisionNotes: v.decisionNotes ?? null,
      })).data,
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e, 'Não foi possível decidir')),
  })

  const convertMutation = useMutation({
    mutationFn: async () => (await api.post(`/production-requests/${convert?.id}/convert`, {
      qtyPlanned: form.qtyPlanned ? Number(form.qtyPlanned) : null,
      scheduledFor: form.scheduledFor || null,
      expiryDate: form.expiryDate || null,
      notes: form.notes.trim() || null,
    })).data as { id: string; number: string },
    onSuccess: (row) => {
      invalidate()
      setConvert(null)
      navigate(`/industria/ops/${row.id}`)
    },
    onError: (e) => setError(apiError(e, 'Não foi possível gerar a ordem')),
  })

  const pending = requests.filter(r => r.status === 'requested').length

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Solicitações de produção</h2>
        <p className="text-sm text-gray-500">
          A fila do que as marcas pediram pelo portal — aceitar e converter em ordem
        </p>
      </div>

      {pending > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <Inbox className="h-4 w-4 shrink-0" />
          {pending} solicitação(ões) esperando resposta.
        </div>
      )}

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">{requests.length} solicitação(ões)</span>
          <select
            className="h-9 rounded-lg border border-gray-300 px-2 text-sm"
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            <option value="">Todas</option>
            {STATUSES.map(s => <option key={s} value={s}>{REQUEST_STATUS_LABEL[s]}</option>)}
          </select>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : requests.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <Inbox className="mx-auto mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-400">Nenhuma solicitação</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Quando</th>
                    <th className="px-4 py-2.5 text-left font-medium">Marca</th>
                    <th className="px-4 py-2.5 text-left font-medium">Produto</th>
                    <th className="px-4 py-2.5 text-right font-medium">Quantidade</th>
                    <th className="px-4 py-2.5 text-left font-medium">Data desejada</th>
                    <th className="px-4 py-2.5 text-left font-medium">Situação</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {requests.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5 text-gray-500">
                        {fmtDateTime(r.createdAt)}
                        {r.author && <span className="block text-xs text-gray-400">{r.author}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">{r.tenantName}</td>
                      <td className="px-4 py-2.5">
                        <span className="text-gray-900">{r.productName}</span>
                        <span className="block font-mono text-xs text-gray-400">{r.productSku}</span>
                        {!r.hasActiveRecipe && (
                          <span className="mt-1 inline-flex items-center gap-1 text-xs text-amber-700">
                            <AlertTriangle className="h-3 w-3" /> sem ficha ativa
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {r.qty.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{fmtDate(r.desiredDate)}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant={REQUEST_STATUS_BADGE[r.status]}>{REQUEST_STATUS_LABEL[r.status]}</Badge>
                        {r.orderNumber && (
                          <Link
                            to={`/industria/ops/${r.productionOrderId}`}
                            className="mt-1 block font-mono text-xs text-blue-600 hover:underline"
                          >
                            {r.orderNumber}
                          </Link>
                        )}
                        {r.decisionNotes && (
                          <span className="mt-1 block text-xs text-gray-400">{r.decisionNotes}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {canEdit && (
                          <div className="flex justify-end gap-1">
                            {r.status === 'requested' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    const reason = window.prompt('Motivo da recusa (opcional):') ?? undefined
                                    setError(null)
                                    decideMutation.mutate({ id: r.id, status: 'rejected', decisionNotes: reason })
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  loading={decideMutation.isPending}
                                  onClick={() => { setError(null); decideMutation.mutate({ id: r.id, status: 'accepted' }) }}
                                >
                                  <Check className="h-4 w-4" /> Aceitar
                                </Button>
                              </>
                            )}
                            {['requested', 'accepted'].includes(r.status) && (
                              <Button
                                size="sm"
                                disabled={!r.hasActiveRecipe}
                                title={r.hasActiveRecipe ? '' : 'O produto precisa de ficha técnica ativa'}
                                onClick={() => {
                                  setForm({
                                    qtyPlanned: String(r.qty),
                                    scheduledFor: r.desiredDate ?? '',
                                    expiryDate: '',
                                    notes: r.notes ?? '',
                                  })
                                  setError(null)
                                  setConvert(r)
                                }}
                              >
                                <Factory className="h-4 w-4" /> Gerar OP
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal title="Gerar ordem de produção" open={!!convert} onClose={() => setConvert(null)}>
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); convertMutation.mutate() }}
        >
          {convert && (
            <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {convert.tenantName} · {convert.productName}
              <span className="block text-xs text-gray-500">
                pedido de {convert.qty.toLocaleString('pt-BR')} un
                {convert.desiredDate ? ` para ${fmtDate(convert.desiredDate)}` : ''}
              </span>
            </p>
          )}

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Quantidade"
              type="number"
              min={1}
              value={form.qtyPlanned}
              onChange={e => setForm({ ...form, qtyPlanned: e.target.value })}
              required
            />
            <Input
              label="Programar para"
              type="date"
              value={form.scheduledFor}
              onChange={e => setForm({ ...form, scheduledFor: e.target.value })}
            />
            <Input
              label="Validade"
              type="date"
              value={form.expiryDate}
              onChange={e => setForm({ ...form, expiryDate: e.target.value })}
            />
          </div>

          <Input label="Observações" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />

          <p className="text-xs text-gray-500">
            A ordem nasce planejada, com a ficha ativa do produto congelada nela. A reserva de
            material só acontece na liberação.
          </p>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setConvert(null)}>Cancelar</Button>
            <Button type="submit" loading={convertMutation.isPending}>Gerar ordem</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
